import { Injectable, inject, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { StorageService } from './storage.service';
import { environment } from '../../environments/environment';
import { UiService } from './ui.service';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class GuestTokenService {
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);
  private guestKey = 'guest_token';
  private authKey = 'authToken'; // real user token (if logged in)
  private storageService = inject(StorageService);
  private uiService = inject(UiService);
  private refreshTimer: any = null;
  private guestRefreshTimer: any = null;
  private authService = inject(AuthService);
  get guestToken(): string | null {
    return this.storageService.getItem(this.guestKey);
  }

  get authToken(): string | null {
    return this.storageService.getItem(this.authKey);
  }
  /**  Decode JWT and check expiry */
  isAuthTokenExpired(): boolean {
    return this.isTokenExpired(this.authToken);
  }

  /** Check if the auth token is valid but will expire in less than 5 minutes */
  isAuthTokenAboutToExpire(): boolean {
    if (!this.authToken) return false;

    try {
      const payload = JSON.parse(atob(this.authToken.split('.')[1]));
      const exp = payload.exp;
      
      if (!exp) return false;

      const now = Math.floor(Date.now() / 1000);
      const minutesUntilExpiry = (exp - now) / 60;
      
      // true if it expires in less than 2 minutes, including if it has already expired
      // (e.g. laptop wakes from sleep)
      return minutesUntilExpiry <= 2;
    } catch (e) {
      return false;
    }
  }

  isGuestTokenExpired(): boolean {
    return this.isTokenExpired(this.guestToken);
  }

  private isTokenExpired(token: string | null): boolean {
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1])); // decode JWT payload
      const exp = payload.exp; // expiry timestamp in seconds
      
      if (!exp) return true;

      const now = Math.floor(Date.now() / 1000);
      return exp < now; // true if expired
    } catch (e) {
      return true; // treat as expired if invalid
    }
  }

  /**  Ensure valid token (call on app init or before API calls) */
  ensureValidToken(): void {
    // We no longer manually clear the auth token here.
    // If it's expired, we rely on the HTTP interceptor to catch the 401 
    // and use the 7-day Refresh Token to get a new Access Token.
  }

  async fetchGuestToken(): Promise<string | void> {
    //  If auth token exists and still valid, skip
    if (this.authToken && !this.isAuthTokenExpired()) {
      if (this.guestToken) {
        this.storageService.removeItem(this.guestKey);
      }
      return;
    }

    // We no longer clear the auth token here if it's expired.
    // Why? Because the user might have a valid 7-day Refresh Token in their HTTP-only cookies.
    // If we clear it here on app startup, the interceptor never gets a chance to call /api/auth/refresh.
    // We let the first API call fail with 401 (or trigger the proactive refresh check) so the interceptor handles it.

    try {
      const res = await firstValueFrom(
        this.http.post<{ guest_token: string }>(
          `${environment.apiURL}/api/guest-token`,
          {}
        )
      );
      this.storageService.setItem(this.guestKey, res.guest_token);
      this.startGuestAutoRefresh(); // auto-renew before next expiry
      return res.guest_token;
    } catch (err: any) {
      this.uiService.showToast(err.message || 'Failed to fetch guest token');
      throw err;
    }
  }

  /** Gets a valid guest token. Fetches a new one if expired or missing. */
  async getValidGuestToken(): Promise<string | null> {
    if (this.authToken && !this.isAuthTokenExpired()) {
        return null; // User is logged in, no guest token needed
    }

    if (!this.guestToken || this.isGuestTokenExpired()) {
       await this.fetchGuestToken();
    }
    
    return this.guestToken;
  }

  /** Call this after login */
  setAuthToken(token: string): void {
    this.storageService.setItem(this.authKey, token);

    // Remove guest token if it exists
    if (this.guestToken) {
      this.storageService.removeItem(this.guestKey);
    }

    // Stop guest refresh since user is now logged in
    this.stopGuestAutoRefresh();

    // Start background auto-refresh for auth token
    this.startAutoRefresh();
  }

  /** Call this after logout */
  async clearAuthToken(): Promise<void> {
    this.stopAutoRefresh();
    this.storageService.clear();
    this.storageService.removeItem(this.authKey);
    // fetch a new guest token again
    await this.fetchGuestToken();
  }

  /**
   * Background auto-refresh: checks every 60s if token is about to expire.
   * If yes, automatically calls /api/auth/refresh to get a new token.
   * This ensures the token stays valid even when user is idle.
   */
  startAutoRefresh(): void {
    this.stopAutoRefresh(); // clear any existing timer

    this.ngZone.runOutsideAngular(() => {
      this.refreshTimer = setInterval(() => {
        if (this.authToken && this.isAuthTokenAboutToExpire()) {
          this.ngZone.run(() => {
            // console.log('🔄 Auto-refreshing token...');
            this.authService.refreshToken().subscribe({
              next: (res) => {
                if (res?.access_token) {
                  this.setAuthToken(res.access_token);
                  // console.log('✅ Token auto-refreshed successfully');
                }
              },
              error: (err) => {
                this.authService.logout().subscribe({ error: () => {} });
                this.clearAuthToken();
                console.error('❌ Auto-refresh failed:', err);
              }
            });
          });
        }
      }, 60000); // check every 60 seconds
    });
  }

  stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Auto-refresh guest token every 55 minutes (token expires in 1 hour).
   * Ensures guest users always have a valid token.
   */
  startGuestAutoRefresh(): void {
    this.stopGuestAutoRefresh(); // clear any existing timer

    this.ngZone.runOutsideAngular(() => {
      this.guestRefreshTimer = setInterval(() => {
        // Only refresh if user is NOT logged in
        if (!this.authToken || this.isAuthTokenExpired()) {
          this.ngZone.run(() => {
            this.fetchGuestToken().catch((err) => {
              console.error('❌ Guest token auto-refresh failed:', err);
            });
          });
        }
      }, 55 * 60 * 1000); // 55 minutes
    });
  }

  stopGuestAutoRefresh(): void {
    if (this.guestRefreshTimer) {
      clearInterval(this.guestRefreshTimer);
      this.guestRefreshTimer = null;
    }
  }
}
